import { getConfig } from '@/lib/config';
import { trackEvent } from '@/lib/mixpanel';
import { notifyUserAction, wsAnalyticsListeners } from '@/lib/ws-analytics';
import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  InMemoryCache,
  Observable,
  Operation,
  createHttpLink,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

const makeHttpLink = (apiUrl: string) =>
  createHttpLink({
    uri: apiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

const makeHttpGetLink = (apiUrl: string) =>
  createHttpLink({
    uri: apiUrl,
    headers: { 'Content-Type': 'application/json' },
    useGETForQueries: true,
  });

const preferGet = (operation: Operation) => {
  const ctx = operation.getContext?.() ?? {};
  return ctx.preferGet === true;
};

// WebSocket subscription link. We intentionally configure resilience knobs that
// graphql-ws leaves off by default — without them the socket has no keep-alive
// (idle proxies/load balancers close it), no ack timeout (a slow upstream looks
// indistinguishable from a dead connection), and the auth header is captured
// once at client-creation time (so any reconnect after the Auth0 token expires
// fails permanently). connectionParams as a function is the documented way to
// refresh credentials on every (re)connect attempt.
//
// Per-subscription scoping (e.g. dealId) is intentionally NOT sent here —
// each subscription already passes its scoping arguments as GraphQL variables,
// which is the right layer for it and avoids the `connectionParams` snapshot
// going stale when the user navigates without closing the socket.
const KEEP_ALIVE_MS = 30_000;
const CONNECTION_ACK_WAIT_MS = 10_000;
const RETRY_ATTEMPTS = 10;

const wsLink = (
  wsUrl: string,
  getAuthorization: () => string | Promise<string>
) =>
  new GraphQLWsLink(
    createClient({
      url: wsUrl,
      lazy: true,
      keepAlive: KEEP_ALIVE_MS,
      connectionAckWaitTimeout: CONNECTION_ACK_WAIT_MS,
      retryAttempts: RETRY_ATTEMPTS,
      connectionParams: async () => ({
        Authorization: await getAuthorization(),
      }),
      on: wsAnalyticsListeners,
    })
  );

const DATE_FIELDS = new Set([
  'couponExpirationDate',
  'createdAt',
  'updatedAt',
  'startDate',
  'startAt',
  'lastViewedAt',
  'lastUpdatedAt',
]);

// Middleware pour transformer les champs DateTime en objets Date
const dateTimeMiddleware = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const sub = forward(operation).subscribe({
      next(response) {
        if (response.data) convertDateFields(response.data);
        observer.next(response);
      },
      error: (err) => observer.error(err),
      complete: () => observer.complete(),
    });
    return () => sub.unsubscribe();
  });
});

// Sanitize a free-text error message before sending to analytics:
// - drop anything that looks like a querystring (often carries tokens/IDs)
// - cap length to keep cardinality bounded
const ERROR_MESSAGE_MAX_LENGTH = 200;
function sanitizeErrorMessage(message: string): string {
  return message.replace(/\?[^\s]*/g, '').slice(0, ERROR_MESSAGE_MAX_LENGTH);
}

const getOperationType = (
  operation: Operation
): 'query' | 'mutation' | 'subscription' | 'unknown' => {
  try {
    const definition = getMainDefinition(operation.query);
    if (definition.kind === 'OperationDefinition') {
      return definition.operation;
    }
  } catch {
    /* fall through */
  }
  return 'unknown';
};

const HTTP_QUERY_RETRY_ATTEMPTS = 2;

const retryReadOnlyQueryLink = new RetryLink({
  attempts: (count, operation, error) =>
    getOperationType(operation) === 'query' &&
    count <= HTTP_QUERY_RETRY_ATTEMPTS &&
    Boolean(error),
  delay: {
    initial: 300,
    max: 2_000,
    jitter: true,
  },
});

const userActionLink = new ApolloLink((operation, forward) => {
  if (getOperationType(operation) === 'mutation') {
    notifyUserAction(operation.operationName || 'unknown');
  }
  return forward(operation);
});

const makeAuthLink = (getAuthorization: () => string | Promise<string>) =>
  setContext(async (_, { headers }) => {
    const Authorization = await getAuthorization();
    return {
      headers: {
        ...headers,
        Authorization,
      },
    };
  });

// APP_GRAPHQL_ERROR is a safety net for analytics: most business errors are
// already tracked by their domain-specific *_FAILED event (DEAL_VALIDATION_FAILED,
// IMPORT_FAILED, DEAL_ITEM_CELL_EDIT_FAILED, …). Queries without a dedicated
// failure event still surface here.
//
// Subscription network errors only reach this link AFTER graphql-ws has
// exhausted its retry budget — transient WebSocket blips that auto-recover are
// filtered out by the underlying client, so what we track here is a real,
// terminal failure rather than reconnect noise.
const analyticsErrorLink = onError(({ error, operation }) => {
  const operationName = operation.operationName || 'unknown';
  const operationType = getOperationType(operation);

  if (CombinedGraphQLErrors.is(error)) {
    const errorCodes = Array.from(
      new Set(
        error.errors.map((err) =>
          typeof err.extensions?.code === 'string'
            ? err.extensions.code
            : 'unknown'
        )
      )
    );
    trackEvent('APP_GRAPHQL_ERROR', {
      operationName,
      operationType,
      errorCount: error.errors.length,
      errorCodes,
      errorMessage: sanitizeErrorMessage(error.errors[0]?.message ?? 'unknown'),
      isNetworkError: false,
    });
    return;
  }

  trackEvent('APP_GRAPHQL_ERROR', {
    operationName,
    operationType,
    errorCount: 1,
    errorCodes: [],
    errorMessage: sanitizeErrorMessage(error.message),
    isNetworkError: true,
  });
});

export function convertDateFields(obj: unknown, depth = 0): void {
  if (!obj || typeof obj !== 'object' || depth > 10) return;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      convertDateFields(item, depth + 1);
    }
    return;
  }

  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (DATE_FIELDS.has(key)) {
      if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
      ) {
        (obj as Record<string, unknown>)[key] = new Date(value);
      }
    } else if (value && typeof value === 'object') {
      convertDateFields(value, depth + 1);
    }
  }
}

// Créer le cache avec configuration pour limiter la mémoire
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        deal: {
          merge(existing, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

export const makeApolloClient = (
  authorization: string,
  getAuthorization?: () => string | Promise<string>
): ApolloClient => {
  const { apiUrl, wsUrl } = getConfig();

  const httpPostLink = makeHttpLink(apiUrl);
  const httpGetLink = makeHttpGetLink(apiUrl);

  // Fall back to the snapshot token if no getter was passed (keeps existing
  // call sites working); when a getter is provided, HTTP operations and
  // WebSocket reconnects pull an expiry-aware token instead of reusing an
  // expired snapshot.
  const resolveAuthorization = getAuthorization ?? (() => authorization);
  const httpLink = ApolloLink.from([
    retryReadOnlyQueryLink,
    makeAuthLink(resolveAuthorization),
    split(preferGet, httpGetLink, httpPostLink),
  ]);

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink(wsUrl, resolveAuthorization),
    httpLink
  );

  const link = ApolloLink.from([
    userActionLink,
    analyticsErrorLink,
    dateTimeMiddleware,
    splitLink,
  ]);

  return new ApolloClient({
    link,
    cache,
    assumeImmutableResults: true,
  });
};
